interface Breakpoints {
  mobile: number;
  tablet: number;
  desktop: number;
}

type ScreenSize = 'mobile' | 'tablet' | 'desktop';

export interface ScreenWidthInfo {
  width: number;
  size: ScreenSize;
  isTouch: boolean;
}

export const DEFAULT_BREAKPOINTS: Breakpoints = {
  mobile: 768,
  tablet: 1024,
  desktop: 1025,
};

export class ScreenWidth {
  private static instance: ScreenWidth;
  private breakpoints: Breakpoints;
  private callbacks: Set<(info: ScreenWidthInfo) => void>;
  private resizeTimeout?: number;

  private constructor() {
    this.breakpoints = DEFAULT_BREAKPOINTS;
    this.callbacks = new Set();
    this.initializeListeners();
  }

  public static getInstance(): ScreenWidth {
    if (!ScreenWidth.instance) {
      ScreenWidth.instance = new ScreenWidth();
    }
    return ScreenWidth.instance;
  }

  public setBreakpoints(breakpoints: Partial<Breakpoints>): void {
    this.breakpoints = { ...this.breakpoints, ...breakpoints };
    this.notifyListeners();
  }

  private getWidth(): number {
    if (typeof window === 'undefined') return 0;

    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth || 0;
  }

  private isTouch(): boolean {
    if (typeof window === 'undefined') return false;

    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-ignore - Some browsers might not have msMaxTouchPoints
      navigator.msMaxTouchPoints > 0
    );
  }

  private getScreenSize(width: number): ScreenSize {
    if (width < this.breakpoints.mobile) return 'mobile';
    if (width < this.breakpoints.tablet) return 'tablet';
    return 'desktop';
  }

  public getScreenInfo(): ScreenWidthInfo {
    const width = this.getWidth();
    return {
      width,
      size: this.getScreenSize(width),
      isTouch: this.isTouch(),
    };
  }

  private initializeListeners(): void {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      if (this.resizeTimeout) {
        window.clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = window.setTimeout(() => {
        this.notifyListeners();
      }, 250);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
  }

  private notifyListeners(): void {
    const info = this.getScreenInfo();
    this.callbacks.forEach(callback => callback(info));
  }

  public subscribe(callback: (info: ScreenWidthInfo) => void): () => void {
    this.callbacks.add(callback);
    callback(this.getScreenInfo()); // Initial call

    return () => {
      this.callbacks.delete(callback);
    };
  }
}
